/**
 * Studio social — animation de nos propres comptes de marque.
 *
 * L'écran suit le seul circuit autorisé : rédiger → relire et corriger →
 * valider → copier → publier à la main → enregistrer l'URL publiée.
 * Aucun bouton de cet écran ne poste sur Instagram, X ou LinkedIn.
 */
import { useEffect, useState } from 'react';
import {
  NETWORK_PROFILES,
  SOCIAL_NETWORKS,
  type SocialAccount,
  type SocialNetwork,
  type SocialPostDetail,
  type SocialPostStatus,
} from '@vigie/shared';
import { api } from '../api.js';
import { Card, Empty, Field, Notice } from '../components.js';
import { formatDate, useAction, useAsync } from '../hooks.js';

const STATUS_LABEL: Record<SocialPostStatus, { label: string; className: string }> = {
  draft: { label: 'brouillon', className: '' },
  pending_review: { label: 'à relire', className: 'accent' },
  approved: { label: 'validé', className: 'ok' },
  scheduled: { label: 'programmé', className: 'warn' },
  published: { label: 'publié', className: 'ok' },
  rejected: { label: 'rejeté', className: 'danger' },
};

/** Ordre de traitement : ce qui attend une décision humaine d'abord. */
const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: 'pending_review', label: 'À relire' },
  { value: 'approved', label: 'Validés' },
  { value: 'scheduled', label: 'Programmés' },
  { value: 'draft', label: 'Brouillons' },
  { value: 'published', label: 'Publiés' },
  { value: 'rejected', label: 'Rejetés' },
  { value: '', label: 'Tous' },
];

/**
 * Les octets du visuel passent par un fetch authentifié : un `<img src>` direct
 * vers l'API n'emporterait pas le cookie de session.
 */
function AssetPreview({ post }: { post: SocialPostDetail }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const assetId = post.asset?.id ?? null;
  const externalUrl = post.asset?.externalUrl ?? null;

  useEffect(() => {
    if (!assetId) return;
    if (externalUrl) {
      setUrl(externalUrl);
      return;
    }
    let revoked: string | null = null;
    let cancelled = false;
    api
      .socialAssetUrl(assetId)
      .then((objectUrl) => {
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        revoked = objectUrl;
        setUrl(objectUrl);
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      // Libérer l'URL objet : sinon chaque rechargement fuit un blob.
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [assetId, externalUrl]);

  if (!assetId) return null;
  if (failed) return <div className="small muted">Visuel illisible.</div>;
  if (!url) return <div className="small muted">Chargement du visuel…</div>;
  return (
    <img
      src={url}
      alt={post.asset?.altText ?? ''}
      style={{ maxWidth: 260, borderRadius: 6, border: '1px solid var(--border)' }}
    />
  );
}

function PostCard({
  post,
  canGenerateImage,
  canStoreImage,
  onChanged,
}: {
  post: SocialPostDetail;
  canGenerateImage: boolean;
  canStoreImage: boolean;
  onChanged: () => void;
}) {
  const { run, busy, error } = useAction();
  const [body, setBody] = useState(post.body);
  const [hashtags, setHashtags] = useState(post.hashtags.join(' '));
  const [imagePrompt, setImagePrompt] = useState(post.imagePrompt ?? '');
  const [scheduledFor, setScheduledFor] = useState(
    post.scheduledFor ? post.scheduledFor.slice(0, 16) : '',
  );
  const [publishedUrl, setPublishedUrl] = useState(post.publishedUrl ?? '');
  const [copied, setCopied] = useState<string | null>(null);

  const profile = NETWORK_PROFILES[post.network];
  const dirty = body !== post.body || hashtags !== post.hashtags.join(' ');
  const overLimit = body.length > profile.maxLength;
  const locked = post.status === 'published';

  const tagList = () =>
    hashtags
      .split(/[\s,]+/)
      .map((tag) => tag.replace(/^#+/, ''))
      .filter((tag) => tag !== '');

  const copy = async (text: string, what: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(what);
    setTimeout(() => setCopied(null), 2000);
  };

  const save = async () => {
    const saved = await run(() =>
      api.updateSocialPost(post.id, { body, hashtags: tagList(), imagePrompt }),
    );
    if (saved) onChanged();
  };

  const importImage = async (file: File) => {
    const dataBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('Lecture du fichier impossible'));
      reader.readAsDataURL(file);
    });
    const asset = await run(() =>
      api.uploadSocialAsset(post.siteId, { dataBase64, mimeType: file.type }),
    );
    if (asset) {
      await run(() => api.updateSocialPost(post.id, { assetId: asset.id }));
      onChanged();
    }
  };

  /** Texte prêt à coller : corps puis hashtags, comme sur la plateforme. */
  const fullText = [body, tagList().map((tag) => `#${tag}`).join(' ')]
    .filter((part) => part !== '')
    .join('\n\n');

  return (
    <Card
      title={
        <div className="inline">
          <span className="badge accent">{profile.label}</span>
          <strong>{post.accountHandle}</strong>
          <span className={`badge ${STATUS_LABEL[post.status].className}`}>
            {STATUS_LABEL[post.status].label}
          </span>
          <span className="small muted">{post.siteName}</span>
        </div>
      }
      actions={
        <div className="inline">
          <button type="button" className="small" onClick={() => copy(fullText, 'texte')}>
            {copied === 'texte' ? 'Copié ✓' : 'Copier'}
          </button>
          {!locked ? (
            <button
              type="button"
              className="small danger"
              disabled={busy}
              onClick={async () => {
                if (!confirm('Supprimer définitivement ce post ?')) return;
                await run(() => api.deleteSocialPost(post.id));
                onChanged();
              }}
            >
              Supprimer
            </button>
          ) : null}
        </div>
      }
    >
      {error ? <Notice kind="error">{error}</Notice> : null}

      <div className="detail-grid">
        <div className="stack">
          <Field
            label="Texte du post"
            help={
              <span className={overLimit ? 'badge danger' : 'muted'}>
                {body.length} / {profile.maxLength} caractères
              </span>
            }
          >
            <textarea
              rows={8}
              value={body}
              disabled={locked}
              onChange={(event) => setBody(event.target.value)}
            />
          </Field>

          <Field
            label={`Hashtags (${profile.maxHashtags} au maximum)`}
            help="Séparés par des espaces, sans le caractère #."
          >
            <input
              value={hashtags}
              disabled={locked}
              placeholder="monitoring ssl"
              onChange={(event) => setHashtags(event.target.value)}
            />
          </Field>

          {!locked ? (
            <div className="inline">
              <button type="button" onClick={save} disabled={busy || !dirty || overLimit}>
                {dirty ? 'Enregistrer les corrections' : 'Aucune modification'}
              </button>
            </div>
          ) : null}
        </div>

        <div className="stack">
          <AssetPreview post={post} />

          {!locked ? (
            <>
              <Field
                label="Visuel"
                help={
                  canGenerateImage
                    ? "Le prompt est produit avec le texte ; corrigez-le avant de générer."
                    : "Aucun fournisseur d'images configuré : importez un visuel ou posez OPENAI_API_KEY / GEMINI_API_KEY."
                }
              >
                <textarea
                  rows={3}
                  value={imagePrompt}
                  placeholder="Description du visuel, en anglais"
                  onChange={(event) => setImagePrompt(event.target.value)}
                />
              </Field>

              <div className="inline">
                <button
                  type="button"
                  className="small"
                  disabled={busy || !canGenerateImage || !canStoreImage || imagePrompt.trim() === ''}
                  onClick={async () => {
                    const updated = await run(() =>
                      api.generateSocialImage(post.id, { prompt: imagePrompt }),
                    );
                    if (updated) onChanged();
                  }}
                >
                  {busy ? 'Génération…' : 'Générer le visuel'}
                </button>

                <label className="small" style={{ cursor: canStoreImage ? 'pointer' : 'not-allowed' }}>
                  Importer une image
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={busy || !canStoreImage}
                    style={{ display: 'none' }}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void importImage(file);
                      event.target.value = '';
                    }}
                  />
                </label>
              </div>
            </>
          ) : null}

          {post.trackingUrl ? (
            <Field label="URL taguée à insérer" help="Sans elle, cette publication ne sera pas mesurable.">
              <div className="inline">
                <input className="mono small" readOnly value={post.trackingUrl} />
                <button
                  type="button"
                  className="small"
                  onClick={() => copy(post.trackingUrl!, 'url')}
                >
                  {copied === 'url' ? 'Copié ✓' : 'Copier'}
                </button>
              </div>
            </Field>
          ) : null}
        </div>
      </div>

      {/* Décisions humaines : c'est la seule barrière avant publication. */}
      {!locked ? (
        <div className="row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
          <Field label="Date visée (indicative)" help="Aucune publication ne part toute seule.">
            <input
              type="datetime-local"
              value={scheduledFor}
              onChange={(event) => setScheduledFor(event.target.value)}
            />
          </Field>
          <div className="shrink">
            <button
              type="button"
              className="primary"
              disabled={busy || dirty || overLimit}
              title={dirty ? 'Enregistrez vos corrections avant de valider' : undefined}
              onClick={async () => {
                const updated = await run(() =>
                  api.approveSocialPost(
                    post.id,
                    scheduledFor ? new Date(scheduledFor).toISOString() : null,
                  ),
                );
                if (updated) onChanged();
              }}
            >
              Valider
            </button>
          </div>
          <div className="shrink">
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                const notes = prompt('Pourquoi ce post est-il rejeté ? (facultatif)') ?? '';
                const updated = await run(() => api.rejectSocialPost(post.id, notes));
                if (updated) onChanged();
              }}
            >
              Rejeter
            </button>
          </div>
        </div>
      ) : null}

      {post.status === 'approved' || post.status === 'scheduled' ? (
        <div className="row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
          <Field
            label="URL de la publication, une fois postée à la main"
            help="Vigie ne publie pas : copiez le texte, publiez depuis votre compte, puis collez l’URL ici."
          >
            <input
              value={publishedUrl}
              placeholder="https://…"
              onChange={(event) => setPublishedUrl(event.target.value)}
            />
          </Field>
          <div className="shrink">
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                const updated = await run(() =>
                  api.markSocialPostPublished(post.id, publishedUrl || undefined),
                );
                if (updated) onChanged();
              }}
            >
              J’ai publié
            </button>
          </div>
        </div>
      ) : null}

      {post.status === 'published' ? (
        <div className="small muted" style={{ marginTop: 8 }}>
          Publié le {formatDate(post.publishedAt)}
          {post.publishedUrl ? (
            <>
              {' · '}
              <a href={post.publishedUrl} target="_blank" rel="noreferrer">
                voir la publication ↗
              </a>
            </>
          ) : null}
        </div>
      ) : null}

      {post.reviewNotes ? (
        <div className="small muted" style={{ marginTop: 8 }}>
          Note de relecture : {post.reviewNotes}
        </div>
      ) : null}
    </Card>
  );
}

function AccountsCard({
  siteId,
  accounts,
  onChanged,
}: {
  siteId: string;
  accounts: SocialAccount[];
  onChanged: () => void;
}) {
  const { run, busy, error } = useAction();
  const [network, setNetwork] = useState<SocialNetwork>('linkedin');
  const [handle, setHandle] = useState('');
  const [instructions, setInstructions] = useState('');

  return (
    <Card title="Comptes de marque">
      {error ? <Notice kind="error">{error}</Notice> : null}

      {accounts.length === 0 ? (
        <Empty>Aucun compte enregistré pour ce site. Ajoutez-en un ci-dessous.</Empty>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Réseau</th>
              <th>Compte</th>
              <th>Ton</th>
              <th style={{ width: 90 }} />
            </tr>
          </thead>
          <tbody>
            {accounts.map((account) => (
              <tr key={account.id}>
                <td>{NETWORK_PROFILES[account.network].label}</td>
                <td className="mono">{account.handle}</td>
                <td className="small muted truncate">{account.llmInstructions ?? '—'}</td>
                <td>
                  <button
                    type="button"
                    className="small danger"
                    disabled={busy}
                    onClick={async () => {
                      if (!confirm(`Retirer ${account.handle} ? Ses posts seront supprimés.`)) return;
                      await run(() => api.deleteSocialAccount(account.id));
                      onChanged();
                    }}
                  >
                    Retirer
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className="row" style={{ marginTop: 12, alignItems: 'flex-end' }}>
        <div className="shrink">
          <label>Réseau</label>
          <select
            value={network}
            onChange={(event) => setNetwork(event.target.value as SocialNetwork)}
          >
            {SOCIAL_NETWORKS.map((item) => (
              <option key={item} value={item}>
                {NETWORK_PROFILES[item].label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label>Compte</label>
          <input value={handle} placeholder="@certfleet" onChange={(e) => setHandle(e.target.value)} />
        </div>
        <Field
          label="Ton du compte"
          help="Hérite du positionnement du site et le surcharge, comme les instructions de plateforme."
        >
          <input
            value={instructions}
            placeholder="Sobre, technique, destiné aux MSP."
            onChange={(event) => setInstructions(event.target.value)}
          />
        </Field>
        <div className="shrink">
          <button
            type="button"
            disabled={busy || !handle}
            onClick={async () => {
              const created = await run(() =>
                api.createSocialAccount(siteId, {
                  network,
                  handle,
                  label: handle,
                  llmInstructions: instructions || null,
                }),
              );
              if (created) {
                setHandle('');
                setInstructions('');
                onChanged();
              }
            }}
          >
            Ajouter
          </button>
        </div>
      </div>
    </Card>
  );
}

export function Studio() {
  const sites = useAsync(() => api.sites(), []);
  const [siteId, setSiteId] = useState('');
  const activeSite = siteId || sites.data?.[0]?.id || '';

  const capabilities = useAsync(() => api.socialCapabilities(), []);
  const accounts = useAsync(
    () => (activeSite ? api.socialAccounts(activeSite) : Promise.resolve([])),
    [activeSite],
  );
  const [status, setStatus] = useState('pending_review');
  const posts = useAsync(
    () => (activeSite ? api.socialPosts({ siteId: activeSite, status }) : Promise.resolve([])),
    [activeSite, status],
  );

  const { run, busy, error } = useAction();
  const [accountId, setAccountId] = useState('');
  const [brief, setBrief] = useState('');
  const [count, setCount] = useState(3);

  const accountList = accounts.data ?? [];
  const activeAccount = accountId || accountList[0]?.id || '';

  const generate = async () => {
    const created = await run(() =>
      api.generateSocialPosts({ accountId: activeAccount, brief, count }),
    );
    if (created) {
      setBrief('');
      setStatus('pending_review');
      posts.reload();
    }
  };

  return (
    <>
      <header className="topbar">
        <h1>Studio social</h1>
        <span className="spacer" />
        <div className="inline">
          <select value={activeSite} onChange={(e) => setSiteId(e.target.value)} style={{ width: 180 }}>
            {(sites.data ?? []).map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            {STATUS_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      <div className="content">
        <Notice kind="rule">
          <strong>Vigie ne publie pas.</strong> Le studio rédige, illustre et prépare ; vous relisez,
          validez, copiez, puis publiez depuis votre compte. Aucun bouton de cet écran n’envoie quoi
          que ce soit à Instagram, X ou LinkedIn.
        </Notice>

        {error ? <Notice kind="error">{error}</Notice> : null}
        {posts.error ? <Notice kind="error">{posts.error}</Notice> : null}
        {capabilities.data && !capabilities.data.storageConfigured ? (
          <Notice kind="error">
            Bucket R2 <span className="mono">VIGIE_MEDIA</span> absent : les visuels ne peuvent être
            ni générés ni importés. Créez-le avec{' '}
            <span className="mono">wrangler r2 bucket create vigie-media</span>. Le reste du studio
            fonctionne.
          </Notice>
        ) : null}

        <AccountsCard siteId={activeSite} accounts={accountList} onChanged={accounts.reload} />

        <Card title="Rédiger">
          {accountList.length === 0 ? (
            <Empty>Enregistrez d’abord un compte de marque ci-dessus.</Empty>
          ) : (
            <div className="stack">
              <div className="row" style={{ alignItems: 'flex-end' }}>
                <div className="shrink">
                  <label>Compte</label>
                  <select value={activeAccount} onChange={(e) => setAccountId(e.target.value)}>
                    {accountList.map((account) => (
                      <option key={account.id} value={account.id}>
                        {NETWORK_PROFILES[account.network].label} · {account.handle}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="shrink">
                  <label>Variantes</label>
                  <select value={count} onChange={(event) => setCount(Number(event.target.value))}>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <Field
                label="Brief"
                help="Le sujet ou l’angle. Chaque variante attaque l’idée différemment — vous gardez celle qui tient."
              >
                <textarea
                  rows={3}
                  value={brief}
                  placeholder="Pourquoi un certificat qui expire un vendredi soir coûte plus cher qu’il n’en a l’air."
                  onChange={(event) => setBrief(event.target.value)}
                />
              </Field>
              <div className="inline">
                <button
                  type="button"
                  className="primary"
                  disabled={busy || !brief.trim() || !activeAccount}
                  onClick={generate}
                >
                  {busy ? 'Rédaction…' : `Rédiger ${count} variante(s)`}
                </button>
              </div>
            </div>
          )}
        </Card>

        {posts.loading ? <div className="muted">Chargement…</div> : null}

        {(posts.data ?? []).map((post) => (
          <PostCard
            key={post.id}
            post={post}
            canGenerateImage={Boolean(capabilities.data?.imageProvider)}
            canStoreImage={Boolean(capabilities.data?.storageConfigured)}
            onChanged={posts.reload}
          />
        ))}

        {posts.data && posts.data.length === 0 && !posts.loading ? (
          <Card>
            <Empty>Aucun post dans cette file.</Empty>
          </Card>
        ) : null}
      </div>
    </>
  );
}
